import {createPortal} from 'react-dom';
type Category='services'|'packages'|'memberships'|'products'|'giftCards'|'discounts';
type CatalogEntry={key:Category;count:number};
const categories:Array<{key:Category;label:string}>=[
  {key:'services',label:'services'},{key:'packages',label:'packages'},
  {key:'memberships',label:'memberships'},{key:'products',label:'products'},
  {key:'giftCards',label:'gift cards'},{key:'discounts',label:'discount codes'},
];
const groups:Array<{name:string;rows:Array<[string,string]>}>=[
  {name:'Adding Line Items',rows:[
    ['2x product name','Add 2 products'],
    ['product name x2','Add 2 products'],
    ['2x product name at 20','Add 2 products at $20 each'],
    ['2x service name at 40','Add 2 service credits at $40 each'],
    ['5x gift card series name at 10','Add 5 gift cards at $10 each'],
    ['account credit','Add account credit'],
    ['30 credit for 10','Add $30 account credit for $10'],
    ['gift card code','Add credit to existing gift card'],
    ['gift card code 2x service at 10',
      'Add 2 service credits to existing gift card at $10 each'],
    ['5 tip employee','Add a $5 tip for employee'],
    ['5% tip employee','Add a 5% tip for employee'],
  ]},
  {name:'Adding Payments',rows:[
    ['cash','Add a full cash payment'],
    ['30 cash','Add a $30 cash payment'],
    ['cash 30','Add a $30 cash payment'],
    ['custom payment type 10','Add $10 custom payment'],
    ['custom payment type 30 ref','Add $30 payment with reference number'],
  ]},
  {name:'Special Commands',rows:[
    ['products','List all products'],
    ['services','List all service credits'],
    ['service credits','List all service credits'],
    ['memberships','List all memberships'],
    ['packages','List all packages'],
    ['gift cards','List all gift cards'],
    ['discount codes','List all discount codes'],
    ['referrer person','Set referrer and use code'],
    ['free!','Set eligible line-item prices to $0'],
    ['close','Close sale'],
  ]},
];
export function SaleTicketHelp({mode,counts,onClose,onHelpFeedback,onBrowse}:{
  mode:'pos'|'omnibar';counts:CatalogEntry[];
  onClose:()=>void;onHelpFeedback:()=>void;onBrowse:(category:Category)=>void;
}){
  const support=()=>{onClose();onHelpFeedback();};
  // Render over the entire app, outside the transformed/clipped Sale Ticket.
  // Appointment popovers must never intercept clicks on the support dialog.
  return createPortal(<div className="ecfs-sale-help-backdrop" onMouseDown={event=>{
    if(event.target===event.currentTarget)onClose();
  }}>
    <section role="dialog" aria-modal="true"
      aria-label={mode==='pos'?'Point of Sale help':'Sale Omnibar Commands'}
      className="ecfs-sale-help-dialog">
      <header><h2>{mode==='pos'?'Point of Sale':'Sale Omnibar Commands'}</h2>
        <button type="button" aria-label="Close sale help" onClick={onClose}>×</button>
      </header>
      <div className="ecfs-sale-help-body">
        {mode==='pos'?<>
          <p>This is the main Point of Sale system for the app.</p>
          <p>Click outside the ticket to minimize it. An open sale remains in
            the Sales top menu so it can be resumed. Complete a sale with
            payment and Close Sale, or cancel it when it should not proceed.</p>
          <h3>Adding items to the Sale</h3>
          <p>Use the Omnibar to find services, products, memberships, packages,
            gift cards and discount codes. The Omnibar help icon lists commands
            and category shortcuts. Services, packages, memberships and account
            credit must have a selected customer to receive the credits. A
            customer is optional for eligible retail and gift card purchases.</p>
          <h3>Account Credit</h3>
          <p>Account Credit purchases must belong to the selected person's
            existing account. Account-credit issuing and renewal payment are
            not yet available in this staging release.</p>
          <h3>Payment Types</h3>
          <p>Card and cash have separate verified settlement requirements.
            Other payment types and gift-card/account-credit tender will appear
            when their corresponding financial workflows are enabled.</p>
          <h3>Splitting Payment across multiple credit cards</h3>
          <p>The planned simple workflow is Card 1, select its charge amount,
            then Card 2 with the remaining balance automatically populated.
            This is not yet enabled. Do not simulate split payments using
            temporary Cash amounts.</p>
          <h3>One customer purchasing an item for another customer</h3>
          <p>Eligible items will provide a share action to assign a purchase
            to another existing customer account. Gift Cards will provide a
            send action for authorized email delivery. These actions require
            verified credit and gift fulfillment before becoming available.</p>
        </>:<>
          <p>Full list of</p>
          <nav className="ecfs-sale-help-categories" aria-label="Full sale item lists">
            {categories.map((x,i)=><span key={x.key}>
              {i>0&&<span aria-hidden="true"> · </span>}
              <button type="button" disabled={!counts.find(c=>c.key===x.key)?.count}
                onClick={()=>{onClose();onBrowse(x.key);}}>
                {x.label}</button>
            </span>)}
          </nav>
          <p>Commands requiring unimplemented financial settlement or pricing
            permissions are reference syntax only, not currently executable.</p>
          {groups.map(group=><section key={group.name}>
            <h3>{group.name}</h3>
            <table><tbody>{group.rows.map(([command,result])=>
              <tr key={command}><th scope="row">{command}</th>
                <td>{result}</td></tr>)}</tbody></table>
          </section>)}
        </>}
      </div>
      <footer>
        <button type="button" onClick={support}>Questions for Support?</button>
        <button type="button" onClick={onClose}>Close</button>
      </footer>
    </section>
  </div>,document.body);
}
